import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatisticsService } from "../service";
import { ErrorToast } from "../../../shared/toasters/toasters";
import { Card, Row, Col, Table } from "react-bootstrap";
import { ApexOptions } from "apexcharts";
import LoadingSpinner from "./LoadingSpinner";
import ReactApexChart from "react-apexcharts";

type Period = "30d" | "6m" | "12m" | "all";
type MapType = "jobs" | "coaches";
type ChartView = "bar" | "column" | "treemap";
interface LocationMapData {
  barChart?: {
    categories: string[];
    series: { name: string; data: number[] }[];
  };
  columnChart?: {
    categories: string[];
    series: { name: string; data: number[] }[];
  };
  treemap?: { series: { data: { x: string; y: number }[] }[] };
  raw?: LocationRaw[];
}

interface LocationRaw {
  locationId: number;
  locationName: string;
  parentId?: number;
  parentName?: string;
  count: number;
}

const LocationMapTab = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>("30d");
  const [mapType, setMapType] = useState<MapType>("jobs");
  const [chartView, setChartView] = useState<ChartView>("bar");
  const [data, setData] = useState<LocationMapData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    StatisticsService.getLocationMap(period, mapType)
      .then(setData)
      .catch(ErrorToast)
      .finally(() => setLoading(false));
  }, [period, mapType]);

  const barOptions: ApexOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, barHeight: "70%" } },
    dataLabels: { enabled: false },
    xaxis: { labels: { formatter: (v) => Math.floor(+v).toString() } },
    yaxis: { labels: { style: { fontSize: "11px" } } },
    colors: ["#4680ff"],
    grid: { borderColor: "#e0e0e0" },
  };

  const columnOptions: ApexOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "60%" } },
    dataLabels: { enabled: false },
    xaxis: { labels: { rotate: -45, style: { fontSize: "11px" } } },
    colors: ["#2ca87f", "#4680ff"],
    legend: { position: "top" },
    grid: { borderColor: "#e0e0e0" },
  };

  const treemapOptions: ApexOptions = {
    chart: { type: "treemap", toolbar: { show: false } },
    colors: ["#4680ff"],
    dataLabels: { enabled: true, style: { fontSize: "12px" } },
    legend: { show: false },
  };

  return (
    <Row className="g-4">
      {/* Filters */}
      <Col xl={12}>
        <Card>
          <Card.Body className="py-3">
            <div className="d-flex flex-wrap gap-3 align-items-center">
              <div className="d-flex gap-2 align-items-center">
                <span className="text-muted small fw-semibold">
                  {t("statistics.period")}:
                </span>
                {(["30d", "6m", "12m", "all"] as Period[]).map((p) => (
                  <button
                    key={p}
                    className={`btn btn-sm ${period === p ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setPeriod(p)}
                  >
                    {t(`statistics.period_${p}`)}
                  </button>
                ))}
              </div>
              <div className="d-flex gap-2 align-items-center">
                <span className="text-muted small fw-semibold">
                  {t("statistics.type")}:
                </span>
                {(["jobs", "coaches"] as MapType[]).map((tp) => (
                  <button
                    key={tp}
                    className={`btn btn-sm ${mapType === tp ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setMapType(tp)}
                  >
                    {t(`statistics.${tp}`)}
                  </button>
                ))}
              </div>
              <div className="d-flex gap-2 align-items-center ms-auto">
                <span className="text-muted small fw-semibold">
                  {t("statistics.view")}:
                </span>
                {(["bar", "column", "treemap"] as ChartView[]).map((v) => (
                  <button
                    key={v}
                    className={`btn btn-sm ${chartView === v ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setChartView(v)}
                  >
                    <i
                      className={`ph-duotone ${v === "bar" ? "ph-chart-bar-horizontal" : v === "column" ? "ph-chart-bar" : "ph-squares-four"} me-1`}
                    />
                    {t(`statistics.view_${v}`)}
                  </button>
                ))}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Chart */}
      <Col xl={8}>
        <Card className="h-100">
          <Card.Header>
            <h6 className="mb-0">
              {t("statistics.locationDistribution")} -{" "}
              {t(`statistics.${mapType}`)} ({t(`statistics.period_${period}`)})
            </h6>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <LoadingSpinner />
            ) : !data ? null : (
              <>
                {chartView === "bar" && data.barChart && (
                  <ReactApexChart
                    options={{
                      ...barOptions,
                      xaxis: {
                        ...barOptions.xaxis,
                        categories: data.barChart.categories,
                      },
                    }}
                    series={data.barChart.series}
                    type="bar"
                    height={Math.max(
                      300,
                      (data.barChart.categories?.length ?? 10) * 28,
                    )}
                  />
                )}
                {chartView === "column" && data.columnChart && (
                  <ReactApexChart
                    options={{
                      ...columnOptions,
                      xaxis: {
                        ...columnOptions.xaxis,
                        categories: data.columnChart.categories,
                      },
                    }}
                    series={data.columnChart.series}
                    type="bar"
                    height={380}
                  />
                )}
                {chartView === "treemap" && data.treemap && (
                  <ReactApexChart
                    options={treemapOptions}
                    series={data.treemap.series}
                    type="treemap"
                    height={380}
                  />
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Raw table */}
      <Col xl={4}>
        <Card className="h-100">
          <Card.Header>
            <h6 className="mb-0">{t("statistics.rawData")}</h6>
          </Card.Header>
          <Card.Body
            className="p-0"
            style={{ maxHeight: 480, overflowY: "auto" }}
          >
            {loading ? (
              <LoadingSpinner />
            ) : (
              <Table hover borderless size="sm" className="mb-0 small">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>{t("statistics.location")}</th>
                    <th>{t("statistics.parent")}</th>
                    <th className="text-end">{t("statistics.count")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.raw ?? []).map((r) => (
                    <tr key={r.locationId}>
                      <td>{r.locationName}</td>
                      <td className="text-muted">{r.parentName ?? "-"}</td>
                      <td className="text-end fw-semibold">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default LocationMapTab;
